/*
 * FTS / TD — dual-handle price slider for Dawn facets.
 * Sits directly before <price-range> and drives its two price inputs,
 * so Dawn's own PriceRange + FacetFiltersForm handle validation, URL and re-render.
 */
if (!customElements.get('price-slider')) {
  class PriceSlider extends HTMLElement {
    connectedCallback() {
      // Find the Dawn <price-range> next to us (sibling first, then anywhere in the same parent)
      let priceRange = this.nextElementSibling;
      if (!priceRange || priceRange.tagName !== 'PRICE-RANGE') {
        priceRange = this.parentElement && this.parentElement.querySelector('price-range');
      }
      if (!priceRange) return console.warn('[price-slider] no <price-range> found');

      // Match by name, not type: themes use number, text or inputmode variants
      this.minNum =
        priceRange.querySelector('input[name*="price.gte"]') || priceRange.querySelectorAll('input')[0];
      this.maxNum =
        priceRange.querySelector('input[name*="price.lte"]') || priceRange.querySelectorAll('input')[1];
      const ranges = this.querySelectorAll('input[type="range"]');

      if (!this.minNum || !this.maxNum || ranges.length < 2) {
        return console.warn('[price-slider] inputs not found', {
          found: priceRange.querySelectorAll('input').length,
          names: [...priceRange.querySelectorAll('input')].map((i) => i.name),
        });
      }

      [this.minRange, this.maxRange] = ranges;
      this.fill = this.querySelector('.price-slider__fill');
      this.loLabel = this.querySelector('[data-lo]');
      this.hiLabel = this.querySelector('[data-hi]');
      this.max = Number(this.dataset.max) || 0;
      this.currency = this.dataset.currency || '£';
      this.fmt = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 });
      if (!this.max) return;

      if (this.minNum.value || this.maxNum.value) this.syncFromNumbers();
      this.paint();

      [this.minRange, this.maxRange].forEach((r) => {
        // Stop drag events reaching the form, otherwise Dawn re-renders mid-drag
        r.addEventListener('input', (e) => {
          e.stopPropagation();
          this.onSlide(r);
        });
        r.addEventListener('change', (e) => {
          e.stopPropagation();
          this.commit(r);
        });
      });

      // Typing in the From/To boxes moves the handles
      [this.minNum, this.maxNum].forEach((n) => n.addEventListener('input', () => this.syncFromNumbers()));
    }

    parse(v) {
      const n = parseFloat(String(v).replace(/,/g, ''));
      return Number.isFinite(n) ? n : null;
    }

    syncFromNumbers() {
      const lo = this.parse(this.minNum.value);
      const hi = this.parse(this.maxNum.value);
      this.minRange.value = lo ?? 0;
      this.maxRange.value = hi ?? this.max;
      this.paint();
    }

    onSlide(r) {
      let lo = Number(this.minRange.value);
      let hi = Number(this.maxRange.value);
      if (lo > hi) {
        if (r === this.minRange) lo = hi;
        else hi = lo;
        this.minRange.value = lo;
        this.maxRange.value = hi;
      }
      // Leave a box empty at the extremes so the filter isn't applied needlessly
      this.minNum.value = lo <= 0 ? '' : lo;
      this.maxNum.value = hi >= this.max ? '' : hi;
      this.paint();
    }

    commit(r) {
      const target = r === this.minRange ? this.minNum : this.maxNum;
      target.dispatchEvent(new Event('input', { bubbles: true }));
      target.dispatchEvent(new Event('change', { bubbles: true }));
    }

    paint() {
      const loVal = Number(this.minRange.value);
      const hiVal = Number(this.maxRange.value);
      const lo = (loVal / this.max) * 100;
      const hi = (hiVal / this.max) * 100;
      if (this.fill) {
        this.fill.style.left = `${lo}%`;
        this.fill.style.right = `${100 - hi}%`;
      }
      if (this.loLabel) this.loLabel.textContent = this.currency + this.fmt.format(loVal);
      if (this.hiLabel) {
        this.hiLabel.textContent = this.currency + this.fmt.format(hiVal) + (hiVal >= this.max ? '+' : '');
      }
      this.minRange.style.zIndex = lo > 90 ? 3 : 2; // keep min handle grabbable at the far right
    }
  }
  customElements.define('price-slider', PriceSlider);
}